import { Component, EventEmitter, Input, Output, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GridModule } from '@progress/kendo-angular-grid';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  WorkflowTemplate,
  WorkflowDecision,
  CreateWorkflowTemplateRequest,
  UpdateWorkflowTemplateRequest,
  CreateWorkflowStepDefinitionRequest
} from '../../models/file-vault.models';

interface StepEditorModel {
  stepName: string;
  description?: string;
  assignedToRole?: string;
  assignedToUserId?: number | null;
  dueDays?: number | null;
  canSkip: boolean;
  requiresComments: boolean;
  allowedDecisions: WorkflowDecision[];
  isOptional: boolean;
  optionalKey?: string;
  requiredPermissions: string[];
}

interface TemplateEditorModel {
  id?: string;
  name: string;
  description?: string;
  entityType: string;
  isActive: boolean;
  steps: StepEditorModel[];
}

@Component({
  selector: 'fv-workflow-template-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, GridModule, ButtonsModule, InputsModule, DropDownsModule, DialogModule],
  template: `
    <div class="workflow-admin">
      <div class="header">
        <div>
          <h2>Workflow Templates</h2>
          <p class="subtitle">Create and manage workflow templates for Change, PartVersion, and DocumentVersion</p>
        </div>
        <div class="actions">
          <label class="toggle">
            <input type="checkbox" [(ngModel)]="showInactive" (change)="loadTemplates()" />
            Show inactive
          </label>
          <button kendoButton [themeColor]="'primary'" (click)="openCreate()">New Template</button>
          <button kendoButton [look]="'flat'" (click)="loadTemplates()">Refresh</button>
        </div>
      </div>

      <div class="loading" *ngIf="isLoading">Loading templates...</div>
      <div class="error" *ngIf="errorMessage">{{ errorMessage }}</div>

      <kendo-grid *ngIf="!isLoading"
                  [data]="templates"
                  [height]="320"
                  class="templates-grid">
        <kendo-grid-column field="name" title="Name" [width]="220"></kendo-grid-column>
        <kendo-grid-column field="entityType" title="Entity" [width]="140"></kendo-grid-column>
        <kendo-grid-column field="isActive" title="Active" [width]="90">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="status" [class.active]="dataItem.isActive">{{ dataItem.isActive ? 'Active' : 'Inactive' }}</span>
          </ng-template>
        </kendo-grid-column>
        <kendo-grid-column title="Steps" [width]="90">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.steps?.length || 0 }}
          </ng-template>
        </kendo-grid-column>
        <kendo-grid-column field="modifiedAt" title="Modified" [width]="160">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.modifiedAt | date:'short' }}
          </ng-template>
        </kendo-grid-column>
        <kendo-grid-column title="Actions" [width]="140">
          <ng-template kendoGridCellTemplate let-dataItem>
            <button kendoButton [look]="'flat'" (click)="openEdit(dataItem)">Edit</button>
            <button kendoButton [look]="'flat'" [themeColor]="'error'" (click)="confirmDelete(dataItem)">Delete</button>
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>

      <kendo-dialog *ngIf="showEditor"
                    [title]="editorTitle"
                    [width]="820"
                    [height]="'80vh'"
                    (close)="closeEditor()">
        <div class="editor">
          <div class="form-grid">
            <div class="form-group">
              <label>Name *</label>
              <input kendoTextBox [(ngModel)]="editor.name" />
            </div>
            <div class="form-group">
              <label>Entity Type *</label>
              <kendo-dropdownlist
                [(ngModel)]="editor.entityType"
                [data]="entityTypeOptions"
                [valuePrimitive]="true">
              </kendo-dropdownlist>
            </div>
            <div class="form-group full">
              <label>Description</label>
              <textarea kendoTextArea [(ngModel)]="editor.description" [rows]="2"></textarea>
            </div>
            <div class="form-group">
              <label class="toggle">
                <input type="checkbox" [(ngModel)]="editor.isActive" />
                Active
              </label>
            </div>
          </div>

          <div class="steps-header">
            <h3>Steps</h3>
            <button kendoButton [themeColor]="'primary'" (click)="addStep()">Add Step</button>
          </div>

          <div class="steps-list" *ngIf="editor.steps.length > 0">
            <div class="step-card" *ngFor="let step of editor.steps; let i = index">
              <div class="step-title">Step {{ i + 1 }}</div>
              <div class="step-grid">
                <div class="form-group">
                  <label>Step Name *</label>
                  <input kendoTextBox [(ngModel)]="step.stepName" />
                </div>
                <div class="form-group">
                  <label>Assigned Role</label>
                  <kendo-dropdownlist
                    [(ngModel)]="step.assignedToRole"
                    [data]="roleOptions"
                    [valuePrimitive]="true">
                  </kendo-dropdownlist>
                </div>
                <div class="form-group">
                  <label>Assigned User Id</label>
                  <input kendoTextBox type="number" [(ngModel)]="step.assignedToUserId" />
                </div>
                <div class="form-group">
                  <label>Due Days</label>
                  <input kendoTextBox type="number" [(ngModel)]="step.dueDays" />
                </div>
                <div class="form-group full">
                  <label>Description</label>
                  <textarea kendoTextArea [(ngModel)]="step.description" [rows]="2"></textarea>
                </div>
                <div class="form-group">
                  <label class="toggle">
                    <input type="checkbox" [(ngModel)]="step.canSkip" />
                    Can Skip
                  </label>
                </div>
                <div class="form-group">
                  <label class="toggle">
                    <input type="checkbox" [(ngModel)]="step.requiresComments" />
                    Requires Comments
                  </label>
                </div>
                <div class="form-group">
                  <label class="toggle">
                    <input type="checkbox" [(ngModel)]="step.isOptional" />
                    Optional Step
                  </label>
                </div>
                <div class="form-group" *ngIf="step.isOptional">
                  <label>Optional Key *</label>
                  <kendo-dropdownlist
                    [(ngModel)]="step.optionalKey"
                    [data]="optionalSignoffOptions"
                    [textField]="'text'"
                    [valueField]="'value'"
                    [valuePrimitive]="true">
                  </kendo-dropdownlist>
                </div>
                <div class="form-group full">
                  <label>Allowed Decisions</label>
                  <div class="decision-list">
                    <label class="decision" *ngFor="let decision of decisionOptions">
                      <input type="checkbox"
                             [checked]="step.allowedDecisions.includes(decision.value)"
                             (change)="onDecisionChange(step, decision.value, $event)" />
                      {{ decision.text }}
                    </label>
                  </div>
                </div>
                <div class="form-group full">
                  <label>Required Permissions</label>
                  <kendo-multiselect
                    [(ngModel)]="step.requiredPermissions"
                    [data]="permissionOptions"
                    [textField]="'name'"
                    [valueField]="'name'"
                    [valuePrimitive]="true">
                  </kendo-multiselect>
                </div>
              </div>
              <button kendoButton [look]="'flat'" [themeColor]="'error'" (click)="removeStep(i)">Remove Step</button>
            </div>
          </div>

          <div class="empty-steps" *ngIf="editor.steps.length === 0">
            Add at least one step to define approvals.
          </div>
        </div>

        <kendo-dialog-actions>
          <button kendoButton (click)="closeEditor()">Cancel</button>
          <button kendoButton [themeColor]="'primary'"
                  (click)="saveTemplate()"
                  [disabled]="isSaving || !canSave">
            {{ isSaving ? 'Saving...' : 'Save' }}
          </button>
        </kendo-dialog-actions>
      </kendo-dialog>

      <kendo-dialog *ngIf="showDeleteConfirm"
                    title="Delete Template"
                    [width]="420"
                    (close)="showDeleteConfirm = false">
        <p>Delete <strong>{{ templateToDelete?.name }}</strong>?</p>
        <kendo-dialog-actions>
          <button kendoButton (click)="showDeleteConfirm = false">Cancel</button>
          <button kendoButton [themeColor]="'error'" (click)="deleteTemplate()">Delete</button>
        </kendo-dialog-actions>
      </kendo-dialog>
    </div>
  `,
  styles: [`
    .workflow-admin {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }

    .subtitle {
      margin: 4px 0 0;
      color: var(--fv-text-secondary);
      font-size: 13px;
    }

    .actions {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .toggle {
      display: flex;
      gap: 6px;
      align-items: center;
      font-size: 12px;
      color: var(--fv-text);
    }

    .templates-grid {
      font-size: 13px;
    }

    .status {
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 11px;
      background: var(--fv-border-subtle);
      color: var(--fv-text-secondary);
    }

    .status.active {
      background: var(--fv-status-success-bg);
      color: var(--fv-status-success-fg);
    }

    .loading {
      padding: 12px;
      color: var(--fv-text-secondary);
    }

    .error {
      color: var(--fv-status-error-fg);
      font-size: 13px;
    }

    .editor {
      max-height: 70vh;
      overflow: auto;
      padding: 8px 8px 0 0;
    }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(200px, 1fr));
      gap: 12px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .form-group.full {
      grid-column: span 2;
    }

    .steps-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin: 16px 0 8px;
    }

    .steps-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .step-card {
      border: 1px solid var(--fv-border);
      border-radius: 8px;
      padding: 12px;
      background: var(--fv-surface-alt);
    }

    .step-title {
      font-weight: 600;
      margin-bottom: 8px;
    }

    .step-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(200px, 1fr));
      gap: 12px;
    }

    .decision-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px 12px;
    }

    .decision {
      display: flex;
      gap: 6px;
      align-items: center;
      font-size: 12px;
      color: var(--fv-text);
    }

    .empty-steps {
      padding: 12px;
      background: var(--fv-surface-hover);
      border-radius: 6px;
      color: var(--fv-text-muted);
      font-size: 13px;
    }

    @media (max-width: 900px) {
      .form-grid,
      .step-grid {
        grid-template-columns: 1fr;
      }

      .form-group.full {
        grid-column: span 1;
      }
    }
  `]
})
export class WorkflowTemplateAdminComponent implements OnInit {
  private api = inject(FileVaultApiService);

  @Input() entityTypeFilter?: string;
  @Output() templateSaved = new EventEmitter<WorkflowTemplate>();

  templates: WorkflowTemplate[] = [];
  showInactive = false;
  isLoading = false;
  errorMessage = '';

  showEditor = false;
  isSaving = false;
  editor: TemplateEditorModel = this.createEmptyTemplate();

  showDeleteConfirm = false;
  templateToDelete?: WorkflowTemplate;

  entityTypeOptions = ['Change', 'PartVersion', 'DocumentVersion'];
  decisionOptions = [
    { text: 'Approve', value: WorkflowDecision.Approve },
    { text: 'Reject', value: WorkflowDecision.Reject },
    { text: 'Request Changes', value: WorkflowDecision.RequestChanges },
    { text: 'Delegate', value: WorkflowDecision.Delegate }
  ];
  optionalSignoffOptions = [
    { text: 'Accounting', value: 'Accounting' },
    { text: 'Procurement', value: 'Procurement' },
    { text: 'Quality', value: 'Quality' },
    { text: 'Software Support', value: 'Software Support' }
  ];
  roleOptions: string[] = [];
  permissionOptions: { name: string }[] = [];

  get editorTitle(): string {
    return this.editor.id ? 'Edit Workflow Template' : 'Create Workflow Template';
  }

  get canSave(): boolean {
    if (!this.editor.name.trim() || !this.editor.entityType || this.editor.steps.length === 0) {
      return false;
    }
    return this.editor.steps.every(step =>
      step.stepName.trim().length > 0 &&
      (step.assignedToRole?.trim() || step.assignedToUserId) &&
      (!step.isOptional || step.optionalKey?.trim())
    );
  }

  ngOnInit(): void {
    this.loadTemplates();
    this.loadCommonRoles();
    this.loadCommonPermissions();
  }

  async loadCommonRoles(): Promise<void> {
    try {
      const roles = await this.api.getCommonRoles().toPromise() ?? [];
      this.roleOptions = roles.map(role => role.name);
    } catch {
      this.roleOptions = [];
    }
  }

  async loadCommonPermissions(): Promise<void> {
    try {
      const permissions = await this.api.getCommonPermissions().toPromise() ?? [];
      this.permissionOptions = permissions.map(permission => ({ name: permission.name }));
    } catch {
      this.permissionOptions = [];
    }
  }

  async loadTemplates(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';
    try {
      const templates = await this.api.getWorkflowTemplates(!this.showInactive).toPromise() ?? [];
      this.templates = this.entityTypeFilter
        ? templates.filter(t => t.entityType === this.entityTypeFilter)
        : templates;
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to load templates';
    } finally {
      this.isLoading = false;
    }
  }

  openCreate(): void {
    this.editor = this.createEmptyTemplate();
    this.showEditor = true;
  }

  openEdit(template: WorkflowTemplate): void {
    this.editor = {
      id: template.id,
      name: template.name,
      description: template.description,
      entityType: template.entityType,
      isActive: template.isActive,
      steps: template.steps.map(step => ({
        stepName: step.stepName,
        description: step.description,
        assignedToRole: step.assignedToRole,
        assignedToUserId: step.assignedToUserId ?? null,
        dueDays: step.dueDays ?? null,
        canSkip: step.canSkip,
        requiresComments: step.requiresComments,
        allowedDecisions: step.allowedDecisions ?? [],
        isOptional: step.isOptional ?? false,
        optionalKey: step.optionalKey,
        requiredPermissions: step.requiredPermissions ?? []
      }))
    };
    this.showEditor = true;
  }

  closeEditor(): void {
    this.showEditor = false;
  }

  addStep(): void {
    this.editor.steps = [
      ...this.editor.steps,
      {
        stepName: '',
        description: '',
        assignedToRole: '',
        assignedToUserId: null,
        dueDays: null,
        canSkip: false,
        requiresComments: false,
        allowedDecisions: [WorkflowDecision.Approve, WorkflowDecision.Reject],
        isOptional: false,
        optionalKey: undefined,
        requiredPermissions: []
      }
    ];
  }

  removeStep(index: number): void {
    this.editor.steps = this.editor.steps.filter((_, i) => i !== index);
  }

  onDecisionChange(step: StepEditorModel, decision: WorkflowDecision, event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const checked = target?.checked ?? false;
    if (checked) {
      step.allowedDecisions = [...step.allowedDecisions, decision];
    } else {
      step.allowedDecisions = step.allowedDecisions.filter(d => d !== decision);
    }
  }

  async saveTemplate(): Promise<void> {
    if (!this.canSave) return;

    this.isSaving = true;
    try {
      const steps = this.editor.steps.map((step, index): CreateWorkflowStepDefinitionRequest => ({
        stepName: step.stepName,
        description: step.description,
        assignedToRole: step.assignedToRole || undefined,
        assignedToUserId: step.assignedToUserId ?? undefined,
        dueDays: step.dueDays ?? undefined,
        canSkip: step.canSkip,
        requiresComments: step.requiresComments,
        allowedDecisions: step.allowedDecisions.length > 0
          ? step.allowedDecisions
          : [WorkflowDecision.Approve, WorkflowDecision.Reject],
        isOptional: step.isOptional,
        optionalKey: step.optionalKey?.trim() || undefined,
        requiredPermissions: step.requiredPermissions ?? []
      }));

      let saved: WorkflowTemplate | undefined;
      if (this.editor.id) {
        const request: UpdateWorkflowTemplateRequest = {
          name: this.editor.name,
          description: this.editor.description,
          isActive: this.editor.isActive,
          steps
        };
        saved = await this.api.updateWorkflowTemplate(this.editor.id, request).toPromise();
      } else {
        const request: CreateWorkflowTemplateRequest = {
          name: this.editor.name,
          description: this.editor.description,
          entityType: this.editor.entityType,
          steps
        };
        saved = await this.api.createWorkflowTemplate(request).toPromise();
      }

      if (!saved) {
        throw new Error('Workflow template save returned no data.');
      }
      this.templateSaved.emit(saved);
      this.showEditor = false;
      await this.loadTemplates();
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to save template';
    } finally {
      this.isSaving = false;
    }
  }

  confirmDelete(template: WorkflowTemplate): void {
    this.templateToDelete = template;
    this.showDeleteConfirm = true;
  }

  async deleteTemplate(): Promise<void> {
    if (!this.templateToDelete) return;
    try {
      await this.api.deleteWorkflowTemplate(this.templateToDelete.id).toPromise();
      this.showDeleteConfirm = false;
      this.templateToDelete = undefined;
      await this.loadTemplates();
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to delete template';
    }
  }

  private createEmptyTemplate(): TemplateEditorModel {
    return {
      name: '',
      description: '',
      entityType: 'Change',
      isActive: true,
      steps: []
    };
  }
}
